import React, { ReactNode, useMemo, useState, useCallback, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { PrivateRoute } from '@feature/auth';
import { cn } from '@utils/utils';
import { useBackgroundManager } from '@shared/hooks/useBackgroundManager';
import { useWebSocketContext } from '@shared/contexts/WebSocketContext';

interface AppLayoutProps {
  children?: ReactNode;
}

export const AppLayout = React.memo(({ children }: AppLayoutProps) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  
  const { isConnected, connectionId } = useWebSocketContext();

  useEffect(() => {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log('🔌 Estado WebSocket:', { isConnected, connectionId });
    }
  }, [isConnected, connectionId]);
  
  const toggleSidebar = useCallback(() => {
    setIsSidebarOpen(prev => !prev);
  }, []);

  const location = useLocation();
  
  useBackgroundManager();
  
  const shouldShowHeader = useMemo(() => location.pathname !== '/home', [location.pathname]);

  const mainContentClasses = useMemo(() => cn(
    "relative transition-all duration-300 flex-1 w-full md:w-auto",
    isSidebarOpen ? "ml-0 md:ml-74" : "ml-0 md:ml-20"
  ), [isSidebarOpen]);

  return (
    <PrivateRoute>
      <div className="relative h-screen overflow-hidden">
        {/* Mobile Sidebar Backdrop Overlay */}
        {isSidebarOpen && (
          <div 
            className="md:hidden fixed inset-0 bg-black/45 z-40 transition-opacity duration-300"
            onClick={toggleSidebar}
          />
        )}

        {/* Sidebar con props */}
        <Sidebar 
          isOpen={isSidebarOpen} 
          onToggle={toggleSidebar}
        />
        {/* Main Content */}
        <div className={cn(mainContentClasses, "h-full flex flex-col min-w-0")}>
          {/* Mobile Header */}
          <div className="md:hidden flex items-center justify-between px-4 py-2 bg-white border-b-2 border-blue-600 h-16 shrink-0 z-20 shadow-sm">
            {/* Hamburger Button */}
            <button 
              onClick={toggleSidebar}
              className="flex items-center justify-center w-11 h-11 rounded-2xl bg-blue-600 hover:bg-blue-700 active:scale-95 text-white transition-all shadow-sm"
              aria-label="Abrir menú"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            {/* Logos */}
            <div className="flex items-center gap-3">
              <img 
                src="/images/logos/edl_logo_blue.png" 
                alt="Earth Data Lab" 
                className="h-8 object-contain"
              />
              <div className="w-[1.5px] h-6 bg-gray-300"></div>
              <img 
                src="/images/logos/jalisco_logo.png"
                alt="jalisco"
                className="h-8 object-contain"
              />
              <img 
                src="/images/logos/edlLogoLogin.png" 
                alt="Jalisco governorate"
                className="h-8 object-contain"
              />
            </div>
          </div>

          {/* Header - Solo se muestra si no estamos en home */}
          {shouldShowHeader && (
            <div className="hidden md:block pt-4 pb-4 ml-5 top-0 absolute z-10">
              <Header />
            </div>
          )}

          {/* Content Area */}
          <div className="p-5 md:p-0 md:pl-8 flex-1 min-h-0 overflow-y-auto">
            {children || <Outlet />}
          </div>
        </div>
      </div>
    </PrivateRoute>
  );
});

AppLayout.displayName = 'AppLayout'; 